import { Check, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { useCopiedFeedback } from '@/Domain/hooks/App/useCopiedFeedback.hook';

interface CopyFieldProps {
  value: string;
  label?: string;
  testid?: string;
  copyTestid?: string;
  onCopy?: () => void;
}

export const CopyField = ({ value, label, testid, copyTestid, onCopy }: CopyFieldProps) => {
  const { t } = useTranslation('common');
  const { copied, markCopied } = useCopiedFeedback();

  const handleCopy = () => {
    if (copied) return;
    navigator.clipboard.writeText(value).then(
      () => {
        markCopied();
        onCopy?.();
      },
      (err) => console.error('Could not copy to clipboard: ', err),
    );
  };

  return (
    <div className="space-y-1.5">
      {label && <Label className="text-xs text-foreground-subtle">{label}</Label>}
      <div className="flex items-center gap-2">
        <Input
          type="text"
          size="sm"
          value={value}
          readOnly
          className="flex-1 !bg-muted font-mono text-sm"
          data-testid={testid}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCopy}
          data-testid={copyTestid}
          className={`shrink-0 ${copied ? 'pointer-events-none' : ''}`}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? t('copied') : t('copy')}
        </Button>
      </div>
    </div>
  );
};
